import { Button } from '@/components/Button';
import { StatePill } from '@/components/state-pill';
import { DeleteAdModal } from '@/features/ad';
import { AdState } from '@/models/ad';
import { TextField, Typography } from '@mui/material';
import { FC, useCallback, useState, useRef } from 'react';
import { Trans } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

type Props = {
  id?: string;
  title?: string;
  state?: AdState;
};

export const AdTopBar: FC<Props> = ({ id, title, state }) => {
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const textFieldRef = useRef<HTMLInputElement>(null);

  const handleCloseDeleteModal = useCallback(() => {
    setOpenDeleteModal(false);
  }, []);

  const handleCopyId = useCallback(() => {
    const textFieldValue = textFieldRef.current?.value || id || '';

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(textFieldValue)
        .then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        })
        .catch((err) => {
          console.error('Failed to copy to clipboard:', err);
          // Fallback: select the text and show a message
          if (textFieldRef.current) {
            textFieldRef.current.select();
            textFieldRef.current.setSelectionRange(0, 99999);
          }
        });
    } else {
      // Fallback for environments without clipboard API
      if (textFieldRef.current) {
        textFieldRef.current.select();
        textFieldRef.current.setSelectionRange(0, 99999);
        try {
          document.execCommand('copy');
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
          console.error('Failed to copy using execCommand:', err);
        }
      }
    }
  }, [id]);

  return (
    <div className="flex flex-col sm:flex-row items-center sm:justify-between mb-8">
      <div className="flex flex-row mb-4 sm:mb-0">
        <div className="flex items-center gap-4">
          <Typography variant="h4" fontWeight={500}>
            {title}
          </Typography>
          {state && state !== 'NONE' ? <StatePill state={state} /> : null}
        </div>
      </div>
      <div className="flex items-center">
        <div className="flex flex-row w-full justify-end">
          <div className="flex flex-row gap-2">
            <div className="flex flex-row gap-1">
              <TextField
                inputRef={textFieldRef}
                id="outlined-size-small"
                defaultValue={id}
                size="small"
              />
              <Button
                variant="contained"
                color={copySuccess ? 'success' : 'primary'}
                onClick={handleCopyId}
                startIcon={copySuccess ? <CheckIcon /> : <ContentCopyIcon />}
              >
                {copySuccess ? (
                  <Trans i18nKey="label.copied" />
                ) : (
                  <Trans i18nKey="label.copy_id" />
                )}
              </Button>
            </div>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setOpenDeleteModal(true)}
              startIcon={<DeleteIcon />}
            >
              <Trans i18nKey="label.delete" />
            </Button>
          </div>
        </div>
      </div>
      <DeleteAdModal
        id={id}
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
      />
    </div>
  );
};
